import type { MongoEntity } from '@adornis/baseql/entities/mongoEntity';
import { css } from '@adornis/chemistry/directives/css';
import { DesignSystem } from '@adornis/chemistry/elements/theming/design';
import '@adornis/digitale-helden-shared/features/animations/client/animation-live.js';
import { stateful, useState } from '@adornis/functional-lit/stateful';
import { useQuery } from '@adornis/functional-lit/use-query';
import { html } from 'lit';
import { firstValueFrom, timer } from 'rxjs';
import { DigitaleHeldenFont } from '../../../client/theme/fonts';
import { createPublishment } from '../api/createPublishment';
import { deletePublishmentByEntity } from '../api/deletePublishmentByEntity';
import { getPublishment } from '../api/getPublishment';
import { Publishment } from '../db/Publishment';

type PerformAction = 'publish' | 'delete_publishment';

export const PublishmentStatus = stateful(
  ({
    entity,
    onAfterDeletePublishment,
    onAfterCreatePublishment,
  }: {
    entity: MongoEntity;
    onAfterDeletePublishment?: () => Promise<void>;
    onAfterCreatePublishment?: () => Promise<void>;
  }) => {
    const [performAction, setPerformAction] = useState<undefined | PerformAction>(undefined);
    const _publishment = useQuery(() => getPublishment(entity._id)({ _id: 1 }), [entity, performAction]);
    let publishment = _publishment.value;

    if (_publishment.loading) return html` lädt... `;

    return html`
      <d-flex space="xs" w100 crossaxis-center>
        ${status(publishment)}
        ${action({
          entityID: entity._id,
          performAction,
          publishment,
          setPerformAction,
          onAfterDeletePublishment,
          onAfterCreatePublishment,
        })}
      </d-flex>
    `;
  },
);

function status(publishment: Publishment | null) {
  const design = DesignSystem.currentTheme.getValue();

  if (publishment) {
    return html`
      <d-flex horizontal crossaxis-center space="md">
        <animation-live .color=${design.colors.accent}></animation-live>
        <d-h3
          bold
          ${css({
            color: design.colors.tone.black,
            padding: `${design.spacing.sm} 0`,
          })}
        >
          Veröffentlicht
        </d-h3>
      </d-flex>
    `;
  }

  return html`
    <d-h3
      bold
      ${css({
        color: design.colors.tone.black,
        textAlign: 'center',
      })}
    >
      Nicht veröffentlicht
    </d-h3>
  `;
}

function action({
  entityID,
  publishment,
  performAction,
  setPerformAction,
  onAfterDeletePublishment,
  onAfterCreatePublishment,
}: {
  entityID: string;
  publishment: Publishment | null;
  performAction: undefined | PerformAction;
  setPerformAction: (newState: PerformAction | undefined) => void;
  onAfterDeletePublishment?: () => Promise<void>;
  onAfterCreatePublishment?: () => Promise<void>;
}) {
  const design = DesignSystem.currentTheme.getValue();

  switch (performAction) {
    case 'publish':
      return html` <d-text> wird veröffentlicht... </d-text> `;
    case 'delete_publishment':
      return html` <d-text> wird offline genommen... </d-text> `;
    default:
      return html`
        <d-text
          ${css({ cursor: 'pointer', color: design.colors.tone.secondaryText, fontFamily: DigitaleHeldenFont.BLACK })}
          @click=${async () => {
            const hasPublishment = !!publishment;

            setPerformAction(hasPublishment ? 'delete_publishment' : 'publish');

            if (hasPublishment) {
              await deletePublishmentByEntity(entityID)();
              await onAfterDeletePublishment?.();
            } else {
              await createPublishment(entityID)(Publishment.allFields);
              await onAfterCreatePublishment?.();
            }

            await firstValueFrom(timer(1000));

            setPerformAction(undefined);
          }}
        >
          ${publishment ? 'Offline nehmen' : 'Jetzt veröffentlichen!'}
        </d-text>
      `;
  }
}
